/**
 * Hardware back-button (Android) / swipe-back handling for overlays and the
 * live inspection checklist.
 *
 * useOverlayBackClose(open, onBack): while an overlay (lightbox, camera,
 *   dialog) is open, pressing back closes it instead of leaving the page.
 * useLeaveGuard(message): pressing back on the page itself asks for
 *   confirmation before actually leaving.
 */
import { useEffect, useRef } from "react";

let overlayCount = 0;
let suppressNextPop = false;

export const useOverlayBackClose = (open, onBack) => {
  const cbRef = useRef(onBack);
  cbRef.current = onBack;

  useEffect(() => {
    if (!open) return;
    overlayCount += 1;
    window.history.pushState({ pscOverlay: true }, "");
    let poppedByBack = false;
    const onPop = () => {
      // A silently-consumed sentinel (StrictMode double-mount cleanup) —
      // not a real back press; don't close the freshly-mounted overlay.
      if (suppressNextPop) { suppressNextPop = false; return; }
      poppedByBack = true;
      overlayCount = Math.max(0, overlayCount - 1);
      window.removeEventListener("popstate", onPop);
      cbRef.current();
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      if (!poppedByBack) {
        overlayCount = Math.max(0, overlayCount - 1);
        // Overlay was closed via its own UI — consume the sentinel entry
        // silently so the next real back press behaves normally.
        if (window.history.state && window.history.state.pscOverlay) {
          suppressNextPop = true;
          window.history.back();
        }
      }
    };
  }, [open]);
};

export const useLeaveGuard = (message) => {
  useEffect(() => {
    // Idempotent: React StrictMode double-runs effects in dev — never stack
    // two sentinel entries or the final "leave" pop lands on the leftover one.
    if (!(window.history.state && window.history.state.pscGuard)) {
      window.history.pushState({ pscGuard: true }, "");
    }
    const onPop = () => {
      if (suppressNextPop) { suppressNextPop = false; return; }
      if (overlayCount > 0) return; // an overlay's own handler deals with it
      if (window.confirm(message)) {
        window.removeEventListener("popstate", onPop);
        // Defer — browsers can ignore history.back() issued synchronously
        // inside a popstate handler.
        setTimeout(() => window.history.back(), 50);
      } else {
        window.history.pushState({ pscGuard: true }, "");
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
